import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
} from "@mui/material";
import {
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    makeDELETERequestOnUserServiceWithQueryParameter,
} from "../../Api/api";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copy from "copy-to-clipboard";

export const PanCard = ({
    panData,
    adminName,
    kycDocs,
    fetchKycDocs,
    currentUser,
    user_type,
    setValue,
    getErrorMessage,
    accountTypeState,
}: any) => {
    const [form, setForm] = useState({
        name: "",
        pan: "",
        father: "",
        dob: "",
        password: "",
    });
    const toastr = require("toastr");
    const [file, setFile] = useState<any>(null);
    const [kraDate, setKraDate] = useState("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isNonResident = accountTypeState === "individual_non_resident";

    const getInitialDocType = () => {
        if (panData?.type === "form_60") return "form_60";
        return "pan";
    };

    const [documentType, setDocumentType] = useState<string>(
        isNonResident ? getInitialDocType() : "pan"
    );

    const isForm60 = documentType === "form_60";

    useEffect(() => {
        if (panData) {
            setForm({
                name: panData?.name || "",
                pan: panData?.uuid || "",
                father: panData?.fathers_name || "",
                dob: panData?.dob || "",
                password: panData?.kyc_doc_plaintext_password || "",
            });
            setKraDate(
                kycDocs.pan_card?.pan_status_response?.response_date_time || ""
            );
            if (panData?.type === "form_60") {
                setDocumentType("form_60");
            } else {
                setDocumentType("pan");
            }
        }
    }, [panData]);

    const handleChange = (e: any) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const handleDocumentTypeChange = (e: any) => {
        setDocumentType(e.target.value);
    };

    const handleSubmit = async () => {
        try {
            const fd = new FormData();

            fd.append("type", documentType);

            if (isForm60) {
                fd.append("fathers_name", form.father);
                fd.append("dob", form.dob);
                fd.append("user_id", currentUser.uuid);
                fd.append("user_type", user_type);
                fd.append("kyc_doc_plaintext_password", form.password || "");

                if (panData?.uuid) {
                    fd.append("uuid", panData.uuid);
                    fd.append("name", panData.name || "");
                } else {
                    fd.append("uuid", "");
                    fd.append("name", "");
                }
            } else {
                fd.append("uuid", form.pan);
                fd.append("name", form.name);
                fd.append("fathers_name", form.father);
                fd.append("dob", form.dob);
                fd.append("user_id", currentUser.uuid);
                fd.append("user_type", user_type);
                fd.append("kyc_doc_plaintext_password", form.password || "");
            }

            if (file) fd.append("image", file);

            let resp;

            if (panData?.uuid) {
                resp = await makePUTRequestOnUserService(
                    "/partneruser/kyc/pan_card/update/",
                    fd
                );
            } else {
                resp = await makePOSTRequestOnUserService(
                    "/partneruser/kyc/pan_card/create/",
                    fd
                );
            }

            if (resp.status === 200 || resp.status === 201) {
                await fetchKycDocs();
                toastr.success(isForm60 ? "Form 60 Saved" : "PAN Saved");
                setValue((prev: number) => prev + 1);
            }
        } catch (err) {
            toastr.error(getErrorMessage(err));
        }
    };

    const handleStatus = async () => {
        try {
            const resp = await makePOSTRequestOnUserService(
                "/partneruser/cvl_pancard/get/",
                {
                    user_type: user_type,
                    user_id: currentUser.uuid,
                    admin_name: adminName,
                }
            );
            if (resp.status === 200) {
                await fetchKycDocs();
                toastr.success("PAN Status fetched");
            }
        } catch (err) {
            toastr.error(getErrorMessage(err));
        }
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);

            const resp =
                await makeDELETERequestOnUserServiceWithQueryParameter(
                    `/partneruser/kyc/pan_card/delete/${currentUser.uuid}/?user_type=${user_type}`
                );

            if (resp.status === 204) {
                setOpenDeleteDialog(false);
                await fetchKycDocs();
                toastr.success(
                    isForm60
                        ? "Form 60 Deleted Successfully"
                        : "PAN Deleted Successfully"
                );
            }
        } catch (err) {
            toastr.error(getErrorMessage(err));
        } finally {
            setDeleting(false);
        }
    };

    const getTitle = () => {
        if (isForm60) return "Pan Card (Form 60)";
        return "Pan Card";
    };

    const getUploadLabel = () => {
        if (isForm60) return "Upload Form 60 image or PDF to preview it here";
        return "Upload a PAN Card image or PDF to preview it here";
    };

    const getUploadButtonText = () => {
        if (isForm60) return "Upload Form 60";
        return "Upload PAN Card";
    };

    const isUploadedFilePdf = file?.type === "application/pdf";

    return (
        <Grid container spacing={4}>
            <Grid item xs={5}>
                <Box
                    sx={{
                        height: 350,
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        background: "#fafafa",
                        overflow: "hidden",
                    }}
                >
                    {file ? (
                        isUploadedFilePdf ? (
                            <Box sx={{ textAlign: "center" }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, color: "text.secondary" }}
                                >
                                    PDF file selected
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        const url = URL.createObjectURL(file);
                                        window.open(url, "_blank");
                                    }}
                                >
                                    View PDF
                                </Button>
                            </Box>
                        ) : (
                            <img
                                src={URL.createObjectURL(file)}
                                alt="preview"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                            />
                        )
                    ) : kycDocs?.pan_card?.image ? (
                        kycDocs.pan_card.extension === "pdf" ? (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    const byteCharacters = atob(
                                        kycDocs.pan_card.image
                                    );
                                    const byteNumbers = new Array(
                                        byteCharacters.length
                                    )
                                        .fill(0)
                                        .map(
                                            (_, i) =>
                                                byteCharacters.charCodeAt(i)
                                        );
                                    const blob = new Blob(
                                        [new Uint8Array(byteNumbers)],
                                        { type: "application/pdf" }
                                    );
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, "_blank");
                                }}
                            >
                                View PDF
                            </Button>
                        ) : (
                            <img
                                src={`data:image/${kycDocs.pan_card.extension};base64,${kycDocs.pan_card.image}`}
                                alt="PAN"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                            />
                        )
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: "center", px: 2 }}
                        >
                            {getUploadLabel()}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ mt: 1 }}>
                    <Button variant="outlined" component="label" fullWidth>
                        {getUploadButtonText()}
                        <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={(e) => setFile(e.target.files?.[0])}
                        />
                    </Button>
                </Box>
            </Grid>

            <Grid item xs={7}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <h2>{getTitle()}</h2>

                    {isNonResident && (
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                            >
                                Select Document Type
                            </Typography>
                            <RadioGroup
                                row
                                value={documentType}
                                onChange={handleDocumentTypeChange}
                            >
                                <FormControlLabel
                                    value="pan"
                                    control={
                                        <Radio
                                            sx={{
                                                "&.Mui-checked": {
                                                    color: "#262349",
                                                },
                                            }}
                                        />
                                    }
                                    label="PAN Card"
                                />
                                <FormControlLabel
                                    value="form_60"
                                    control={
                                        <Radio
                                            sx={{
                                                "&.Mui-checked": {
                                                    color: "#262349",
                                                },
                                            }}
                                        />
                                    }
                                    label="Form 60"
                                />
                            </RadioGroup>
                        </FormControl>
                    )}

                    {!isForm60 && (
                        <TextField
                            fullWidth
                            label="Name on PAN"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                    )}

                    {!isForm60 && (
                        <TextField
                            fullWidth
                            label="Pan Card Number"
                            name="pan"
                            value={form.pan}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                    )}

                    <TextField
                        fullWidth
                        label="Father's Name"
                        name="father"
                        value={form.father}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Date of Birth (DD/MM/YYYY)"
                        name="dob"
                        value={
                            form.dob
                                ? form.dob.split("-").reverse().join("/")
                                : ""
                        }
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Password (optional)"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    {!isForm60 && (
                        <Grid
                            item
                            xs={12}
                            sx={{
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                type="text"
                                label="Date of KRA Validation"
                                name="response_date_time"
                                variant="outlined"
                                fullWidth
                                disabled
                                value={kraDate}
                            />
                            <IconButton
                                sx={{ mb: 2 }}
                                onClick={() => copy(kraDate)}
                            >
                                <ContentCopyIcon color="primary" />
                            </IconButton>
                        </Grid>
                    )}

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                                backgroundColor: "#262349",
                                "&:hover": { backgroundColor: "#1d1a3a" },
                            }}
                        >
                            {panData?.uuid ? "Update" : "Create"}
                        </Button>

                        {!isForm60 && (
                            <Button
                                variant="outlined"
                                onClick={handleStatus}
                            >
                                PAN Status
                            </Button>
                        )}
                    </Box>

                    {panData?.uuid && (
                        <Button
                            variant="outlined"
                            onClick={() => setOpenDeleteDialog(true)}
                            sx={{ mt: 2 }}
                        >
                            Delete
                        </Button>
                    )}
                </Paper>
            </Grid>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: "bold" }}>
                    {isForm60 ? "Delete Form 60" : "Delete Pan Card"}
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        Are you sure you want to delete{" "}
                        {isForm60 ? "this form 60" : "this pan card"}?
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={deleting}
                        sx={{
                            backgroundColor: "#262349",
                            "&:hover": { backgroundColor: "#1d1a3a" },
                        }}
                    >
                        {deleting ? "Deleting..." : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};